"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { cn } from "./utils";
import { buttonVariants } from "./button";

// CJS/ESM interop — mirrors the pattern used in dialog.tsx
const AlertDialogModule = AlertDialogPrimitive as any;
const AlertDialogDefault = AlertDialogModule?.default;
const RadixAlertDialog = Object.assign(
  {},
  typeof AlertDialogDefault === "object" ? AlertDialogDefault : {},
  AlertDialogModule,
);
RadixAlertDialog.Root =
  AlertDialogModule?.Root ??
  AlertDialogDefault?.Root ??
  (typeof AlertDialogDefault === "function" || typeof AlertDialogDefault === "object"
    ? AlertDialogDefault
    : undefined);
RadixAlertDialog.Trigger = AlertDialogModule?.Trigger ?? AlertDialogModule?.default?.Trigger;
RadixAlertDialog.Content = AlertDialogModule?.Content ?? AlertDialogModule?.default?.Content;
RadixAlertDialog.Portal = AlertDialogModule?.Portal ?? AlertDialogModule?.default?.Portal;
RadixAlertDialog.Overlay = AlertDialogModule?.Overlay ?? AlertDialogModule?.default?.Overlay;
RadixAlertDialog.Title = AlertDialogModule?.Title ?? AlertDialogModule?.default?.Title;
RadixAlertDialog.Description = AlertDialogModule?.Description ?? AlertDialogModule?.default?.Description;
RadixAlertDialog.Action = AlertDialogModule?.Action ?? AlertDialogModule?.default?.Action;
RadixAlertDialog.Cancel = AlertDialogModule?.Cancel ?? AlertDialogModule?.default?.Cancel;

const hasRadixAlertDialog =
  typeof RadixAlertDialog?.Root !== "undefined" &&
  typeof RadixAlertDialog?.Trigger !== "undefined" &&
  typeof RadixAlertDialog?.Content !== "undefined";

let hasWarnedMissingRadixAlertDialog = false;
const warnMissingRadixAlertDialog = () => {
  if (hasWarnedMissingRadixAlertDialog) return;
  hasWarnedMissingRadixAlertDialog = true;
  console.warn(
    "[ui/alert-dialog] Radix AlertDialog primitives unavailable. Falling back to basic markup.",
  );
};

function AlertDialog({
  children,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  if (!hasRadixAlertDialog) {
    warnMissingRadixAlertDialog();
    const isOpen = (props as any)?.open;
    if (isOpen === false) return null;
    return (
      <div data-slot="alert-dialog-fallback" {...(props as any)}>
        {children}
      </div>
    );
  }
  // children must be passed through — Trigger/Content register with the
  // Root via React context, but they only mount if they're actually in the
  // JSX tree. Mirrors the pattern used in `dialog.tsx`.
  return (
    <RadixAlertDialog.Root data-slot="alert-dialog" {...props}>
      {children}
    </RadixAlertDialog.Root>
  );
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  if (!hasRadixAlertDialog || typeof RadixAlertDialog?.Trigger === "undefined") {
    warnMissingRadixAlertDialog();
    return <button data-slot="alert-dialog-trigger-fallback" {...(props as any)} />;
  }
  return (
    <RadixAlertDialog.Trigger data-slot="alert-dialog-trigger" {...props} />
  );
}

function AlertDialogPortal({
  children,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  if (!hasRadixAlertDialog || typeof RadixAlertDialog?.Portal === "undefined") {
    warnMissingRadixAlertDialog();
    return (
      <div data-slot="alert-dialog-portal-fallback" {...(props as any)}>
        {children}
      </div>
    );
  }
  // children must pass through — Radix's Portal renders its children into
  // the portal target. Without this the Overlay + Content never mount.
  return (
    <RadixAlertDialog.Portal data-slot="alert-dialog-portal" {...props}>
      {children}
    </RadixAlertDialog.Portal>
  );
}

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  if (!hasRadixAlertDialog || typeof RadixAlertDialog?.Overlay === "undefined") {
    warnMissingRadixAlertDialog();
    return null;
  }
  return (
    <RadixAlertDialog.Overlay
      ref={ref}
      data-slot="alert-dialog-overlay"
      className={cn(
        // No scrim — the alert's own shadow does the visual separation work,
        // matching the macOS alert pattern. Overlay still intercepts clicks
        // outside the alert so click-to-dismiss keeps working.
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[220]",
        className,
      )}
      {...props}
    />
  );
});
AlertDialogOverlay.displayName = "AlertDialogOverlay";

function AlertDialogContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content> & NativeMetadataProps) {
  const { nativeProps, rest } = splitNativeProps(props);
  if (!hasRadixAlertDialog || typeof RadixAlertDialog?.Content === "undefined") {
    warnMissingRadixAlertDialog();
    const fallback = (
      <>
        <div
          data-slot="alert-dialog-overlay-fallback"
          className="fixed inset-0 z-50"
        />
        <div
          data-slot="alert-dialog-content-fallback"
          data-native-role={nativeProps["data-native-role"] ?? "alert"}
          data-native-presentation={nativeProps["data-native-presentation"] ?? "alert"}
          className={cn(
            "bg-background fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-[var(--ui-dialog-radius)] border p-6 shadow-[var(--ui-dialog-shadow)]",
            className,
          )}
          {...nativeProps}
          {...(rest as any)}
        >
          {children}
        </div>
      </>
    );
    if (typeof document !== "undefined") {
      return createPortal(fallback, document.body);
    }
    return fallback;
  }
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <RadixAlertDialog.Content
        data-slot="alert-dialog-content"
        data-native-role={nativeProps["data-native-role"] ?? "alert"}
        data-native-presentation={nativeProps["data-native-presentation"] ?? "alert"}
        className={cn(
          "qs-vibrant-fill data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 !fixed top-[50%] left-[50%] z-[230] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-[16px] pt-[20px] px-[16px] pb-[16px] duration-200 sm:max-w-lg",
          // Apple macOS 26 alert spec — heavier shadow than dialog,
          // tighter outer corner, no hairline border (the `--alert-shadow`
          // 0.5 px component does that work).
          "rounded-[var(--ui-dialog-radius)] shadow-[var(--ui-dialog-shadow)]",
          className,
        )}
        {...nativeProps}
        {...rest}
      >
        {children}
      </RadixAlertDialog.Content>
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function AlertDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  if (!hasRadixAlertDialog || typeof RadixAlertDialog?.Title === "undefined") {
    warnMissingRadixAlertDialog();
    return (
      <div
        data-slot="alert-dialog-title-fallback"
        className={cn("text-lg font-semibold", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixAlertDialog.Title
      data-slot="alert-dialog-title"
      className={cn("text-lg font-semibold", className)}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  if (!hasRadixAlertDialog || typeof RadixAlertDialog?.Description === "undefined") {
    warnMissingRadixAlertDialog();
    return (
      <div
        data-slot="alert-dialog-description-fallback"
        className={cn("text-muted-foreground text-sm", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixAlertDialog.Description
      data-slot="alert-dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function AlertDialogAction({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> & NativeMetadataProps) {
  const { nativeProps, rest } = splitNativeProps(props);
  if (!hasRadixAlertDialog || typeof RadixAlertDialog?.Action === "undefined") {
    warnMissingRadixAlertDialog();
    return (
      <button
        data-native-role={nativeProps["data-native-role"] ?? "button"}
        data-native-prominence={nativeProps["data-native-prominence"] ?? "increased"}
        className={cn(buttonVariants(), className)}
        {...nativeProps}
        {...(rest as any)}
      />
    );
  }
  return (
    <RadixAlertDialog.Action
      data-native-role={nativeProps["data-native-role"] ?? "button"}
      data-native-prominence={nativeProps["data-native-prominence"] ?? "increased"}
      className={cn(buttonVariants(), className)}
      {...nativeProps}
      {...rest}
    />
  );
}

function AlertDialogCancel({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
  if (!hasRadixAlertDialog || typeof RadixAlertDialog?.Cancel === "undefined") {
    warnMissingRadixAlertDialog();
    return (
      <button
        className={cn(buttonVariants({ variant: "outline" }), className)}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixAlertDialog.Cancel
      className={cn(buttonVariants({ variant: "outline" }), className)}
      {...props}
    />
  );
}

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
